/**
 * CRM Renewals — the client contracts coming up for decision.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS PAGE EXISTS
 *
 * `crm.customer_contract` has carried an end date since June, and the only way
 * to see one was to open a customer's profile and read a string. So "which of my
 * accounts renew inside 90 days" — for a business whose revenue is recurring
 * client contracts — could only be answered by opening every profile in turn.
 *
 * Every other risk surface in this CRM looks backwards: spend against last year,
 * bookings against the trailing pace, NPS over twelve months. All of them
 * describe a decision the customer already made. This is the one that looks
 * forward, and a renewal is where an account actually leaves.
 *
 * ⛔ NOT the Hotel Supplier renewals queue. That one is a hotel RATE expiring
 * (`hsp.renewal_task`). This is a CLIENT CONTRACT. The two shared a word for a
 * long time, which is part of why nobody noticed client renewals had no home.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * COVERAGE IS ON THE PAGE, NOT IN A COMMENT
 *
 * Contract rows are entered by hand, so this page will start out covering a
 * fraction of the book. An empty 90-day horizon then reads as "nothing due" when
 * it means "nothing recorded", and that is a worse lie than showing no page at
 * all. The coverage line states the denominator on every load.
 */
import { Link } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { MultiSelect } from '@/features/crm/components/MultiSelect';
import { bandPillClass } from '@/features/crm/lib/health-bands';

interface Renewal {
  customer_id: string;
  name: string;
  account_number: string | null;
  cust_ae: string | null;
  cust_csm: string | null;
  industry: string | null;
  size: string | null;
  total_spend_12m: number | null;
  contract_end_date: string;
  days_out: number;
  contract_type: string;
  vendor: string | null;
  lapsed: boolean;
  contract_count: number;
  health_band: string;
  health_score: number | null;
  materiality: string;
}

interface RenewalsResponse {
  renewals: Renewal[];
  horizon: number;
  horizons: readonly number[];
  coverage: { total: number; withContractDate: number } | null;
  analyticsUnavailable?: boolean;
}

export default function RenewalsPage() {
  const [data, setData] = useState<RenewalsResponse | null>(null);
  const [horizon, setHorizon] = useState(90);
  const [includeLapsed, setIncludeLapsed] = useState(false);
  const [filterAEs, setFilterAEs] = useState<string[]>([]);
  const [filterCSMs, setFilterCSMs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<RenewalsResponse>(
      `/api/renewals?horizon=${horizon}&include_lapsed=${includeLapsed}`,
    )
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError || e instanceof Error
            ? e.message
            : 'Could not load renewals.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [horizon, includeLapsed]);

  const rows = data?.renewals ?? [];

  const aeOptions = useMemo(
    () => uniqueSorted(rows.map((r) => r.cust_ae)),
    [rows],
  );
  const csmOptions = useMemo(
    () => uniqueSorted(rows.map((r) => r.cust_csm)),
    [rows],
  );

  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          (filterAEs.length === 0 ||
            (r.cust_ae != null && filterAEs.includes(r.cust_ae))) &&
          (filterCSMs.length === 0 ||
            (r.cust_csm != null && filterCSMs.includes(r.cust_csm))),
      ),
    [rows, filterAEs, filterCSMs],
  );

  // Value at stake is the reason to look at this page at all, so it is stated
  // over the FILTERED set — a manager narrowing to one CSM should see that
  // CSM's exposure, not the book's.
  const atStake = visible.reduce((s, r) => s + (r.total_spend_12m ?? 0), 0);
  const upcoming = visible.filter((r) => !r.lapsed);
  const lapsed = visible.filter((r) => r.lapsed);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Renewals</h1>
          <p className="page-sub">
            Client contracts reaching their end date. Every other health signal
            in the CRM looks backwards — this is the one that says a decision is
            coming.
          </p>
        </div>
      </div>

      <div className="filter-bar">
        <div className="pill-row" role="group" aria-label="Renewal horizon">
          {(data?.horizons ?? [30, 60, 90, 180, 365]).map((h) => (
            <button
              aria-pressed={horizon === h}
              className={`pill ${horizon === h ? 'active' : ''}`}
              key={h}
              onClick={() => setHorizon(h)}
              type="button"
            >
              {h} days
            </button>
          ))}
        </div>
        <MultiSelect
          onChange={setFilterAEs}
          options={aeOptions}
          placeholder="Account Executive"
          value={filterAEs}
        />
        <MultiSelect
          onChange={setFilterCSMs}
          options={csmOptions}
          placeholder="CSM"
          value={filterCSMs}
        />
        <label className="inline-check">
          <input
            checked={includeLapsed}
            onChange={(e) => setIncludeLapsed(e.target.checked)}
            type="checkbox"
          />
          Show lapsed
        </label>
      </div>

      {/* The denominator, always. See the header comment. */}
      {data?.coverage && (
        <p className="coverage-note" data-testid="renewal-coverage">
          {data.coverage.withContractDate.toLocaleString()} of{' '}
          {data.coverage.total.toLocaleString()} customers have a contract date
          on file
          {data.coverage.withContractDate < data.coverage.total && (
            <>
              {' '}
              — an empty horizon here means nothing is <em>recorded</em>, not
              that nothing is due.
            </>
          )}
        </p>
      )}

      {loading && <p className="muted">Loading renewals…</p>}
      {error && (
        <p className="banner banner-error" role="alert">
          {error}
        </p>
      )}
      {data?.analyticsUnavailable && (
        <p className="banner banner-error" role="alert">
          The renewals data could not be read. The rest of the CRM is
          unaffected.
        </p>
      )}

      {!loading && !error && (
        <>
          <div className="kpi-row">
            <div className="kpi-card">
              <div className="kpi-label">Renewing in {horizon} days</div>
              <div className="kpi-value">{upcoming.length}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Trailing 12m spend at stake</div>
              <div className="kpi-value">{fmtMoneyShort(atStake)}</div>
            </div>
            {includeLapsed && (
              <div className="kpi-card">
                <div className="kpi-label">Lapsed, nothing newer on file</div>
                <div className="kpi-value">{lapsed.length}</div>
              </div>
            )}
          </div>

          {visible.length === 0 ? (
            <p className="empty-state" data-testid="renewals-empty">
              No contract reaches its end date inside {horizon} days for this
              selection.
              {data?.coverage &&
                data.coverage.withContractDate < data.coverage.total &&
                ' Most customers have no contract date recorded yet, so this is not the same as nothing being due.'}
            </p>
          ) : (
            <div className="section">
              <div className="section-body">
                <div className="table-scroll">
                  <table className="data-table" data-testid="renewals-table">
                    <thead>
                      <tr>
                        <th scope="col">Customer</th>
                        <th scope="col">Ends</th>
                        <th scope="col">Countdown</th>
                        <th scope="col">Contract</th>
                        <th scope="col">Health</th>
                        <th className="num" scope="col">
                          12m spend
                        </th>
                        <th scope="col">CSM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((r) => (
                        <tr key={r.customer_id}>
                          <td>
                            <Link to={`/crm/customers/${r.customer_id}`}>
                              {r.name || r.customer_id}
                            </Link>
                            {r.contract_count > 1 && (
                              <span className="muted-inline">
                                {' '}
                                · {r.contract_count} contracts
                              </span>
                            )}
                          </td>
                          <td>{r.contract_end_date}</td>
                          <td>
                            <CountdownCell
                              daysOut={r.days_out}
                              lapsed={r.lapsed}
                            />
                          </td>
                          <td>
                            {r.contract_type}
                            {r.vendor ? ` · ${r.vendor}` : ''}
                          </td>
                          <td>
                            <span className={bandPillClass(r.health_band)}>
                              {r.health_band}
                            </span>
                          </td>
                          <td className="num">
                            {fmtMoneyShort(r.total_spend_12m ?? 0)}
                          </td>
                          <td>{r.cust_csm ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The countdown.
 *
 * Urgency is encoded in TEXT as well as colour — "in 12 days" is legible to
 * somebody who cannot distinguish the amber from the grey, and the colour is
 * reinforcement rather than the only carrier of meaning.
 */
function CountdownCell({
  daysOut,
  lapsed,
}: {
  daysOut: number;
  lapsed: boolean;
}) {
  if (lapsed) {
    return (
      <span className="countdown countdown-lapsed">
        lapsed {Math.abs(daysOut)}d ago
      </span>
    );
  }
  const cls =
    daysOut <= 30
      ? 'countdown-urgent'
      : daysOut <= 90
        ? 'countdown-soon'
        : 'countdown-later';
  return (
    <span className={`countdown ${cls}`}>
      {daysOut === 0 ? 'today' : `in ${daysOut}d`}
    </span>
  );
}

function uniqueSorted(values: Array<string | null>): string[] {
  return [...new Set(values.filter((v): v is string => !!v))].sort();
}

function fmtMoneyShort(v: number): string {
  if (!isFinite(v) || v === 0) return '$0';
  if (v >= 1_000_000_000) return '$' + (v / 1_000_000_000).toFixed(2) + 'B';
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  if (v >= 10_000) return '$' + (v / 1_000).toFixed(0) + 'k';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
